import { HTTPError, TimeoutError } from "@marketplace/http";
import { notifications } from "@marketplace/ui/notifications";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useAccessToken } from "@/modules/auth";

import { IS_DEV } from ".";

// Internal helpers to keep onError simple and reduce cognitive complexity
const showUnauthenticated = (t: (key: string) => string) => {
  return notifications.show({
    variant: "error",
    message: t("errors.unauthenticated")
  });
};

const showUnauthorized = (t: (key: string) => string) => {
  return notifications.show({
    variant: "error",
    message: t("errors.unauthorized")
  });
};

const handleHTTPError = (
  error: HTTPError,
  accessToken: string | null | undefined,
  t: (key: string) => string
): boolean => {
  const status = error.response.status;
  const url = error.request.url ?? "";

  if (status === 401 && !url.includes("refresh-token")) {
    if (IS_DEV) console.error(error);
    return true;
  }

  if (status === 401 && !accessToken) {
    showUnauthenticated(t);
    return true;
  }

  if (status === 403 && accessToken) {
    showUnauthorized(t);
    return true;
  }

  if (status === 404) {
    // Explicitly handled: avoid default notification
    return true;
  }

  return false;
};

const handleQueryMetaError = (
  query: { meta?: Record<string, unknown> },
  error: unknown
): boolean => {
  const message = query.meta?.errorMessage as string | undefined;
  // If errorMessage is not defined, return false to continue with default handling
  if (message === undefined) return false;

  // If errorMessage is an empty string, suppress all notifications
  if (message === "") {
    if (IS_DEV) console.error(error);
    return true;
  }

  // If errorMessage has content, show custom message
  if (IS_DEV) {
    console.error(error);
    return true;
  }

  notifications.show({
    variant: "error",
    message
  });
  return true;
};

export const useAppQueryClient = () => {
  const [accessToken] = useAccessToken();
  const { t } = useTranslation("common");
  // Narrow i18next TFunction to a simple translator to prevent deep type instantiation
  const tr: (key: string) => string = (key) => t(key as any);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Only retry if error is TimeoutError and less than 2 attempts
          return error instanceof TimeoutError && failureCount < 2;
        }
      }
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Check query meta first - if it has errorMessage (even empty string), handle it
        if (
          handleQueryMetaError(
            query as { meta?: Record<string, unknown> },
            error
          )
        ) {
          return;
        }

        // Then handle HTTP errors
        if (error instanceof HTTPError) {
          if (handleHTTPError(error, accessToken, tr)) return;
        }

        // Finally, show default error message
        notifications.show({
          variant: "error",
          message: tr("errors.somethingWentWrong")
        });
      }
    })
  });

  return queryClient;
};
